import { useState } from 'react';
import api from 'src/services/api';

// ─────────────────────────────────────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────────────────────────────────────

export interface InvoiceSaleItem {
  _id: string;
  invoice_hash?: string;
  sale_hash?: string;
  transaction_id?: string | number;
  payment_status_id: string;
  payment_method_id: string;
  total: number;
  installment_value: number | null;   // valor da parcela — chave do bruto em faturas
  net_total: number;
  payer_id_response?: { name?: string };
  client_id_response?: { name?: string };
  createdAt?: string;
  approved_at?: string;
  project_id?: string;
}

export interface InvoiceFinanceItem {
  item_id: number;
  hash: string;
  client: string;
  product: string;
  total: number | null;               // bruto (installment_value + juros + multa embutidos pelo backend)
  installment_value: number | null;   // parcela base (sem juros/multa de atraso)
  juros: number | null;               // juros de mora — geralmente embutido em total
  multa: number | null;               // multa de atraso — geralmente embutida em total
  net_total: number;
  saldo: number;
  sacado: number;
  status: string;
  operation_type: string;
  operation_type_raw: string | null;
  payment_method_id: string;
  fees: number | null;                // juros de parcelamento
  tax: number | null;                 // deducao total do gateway (= total − net_total)
  administration_tax: number | null;
  transaction_tax: number | null;
  antecipation_gap: number;           // NÃO usado no DRE — derivado algebrico
  operation_fee: number;
  date: string;
  dateRef?: string;
  approved_at?: string;
  createdAt?: string;
  client_id?: string | number;
  type?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Resultado do relatório
// ─────────────────────────────────────────────────────────────────────────────

export interface InvoiceChargebackMatch {
  sale: InvoiceSaleItem | null;
  originalCredit: InvoiceFinanceItem | null;
  chargebackEntry: InvoiceFinanceItem;
  refundEntry: InvoiceFinanceItem | null;
  saleBruto: number;
  creditoLiquido: number;
  debitadoChargeback: number;
  taxasDevolvidas: number;
  custoOperacao: number;
  prejuizoTotal: number;
}

export interface InvoiceNotConciliatedSale {
  sale: InvoiceSaleItem;
  estimatedAvailableAt: Date | null;
  isPastDeadline: boolean;
}

export interface InvoiceReport {
  // ── BLOCO 1: Universo de vendas ──────────────────────────────────────────
  totalSalesInstallmentValue: number;   // Σ installment_value das vendas pagas (para banner)
  totalConciliated: number;
  totalNotConciliated: number;
  totalCancelledInFinance: number;

  salesNotConciliated: InvoiceNotConciliatedSale[];
  salesCancelledInFinance: InvoiceFinanceItem[];

  // ── BLOCO 2: Composição do bruto ─────────────────────────────────────────
  totalInstallmentValue: number;        // Σ installment_value dos créditos do extrato
  totalJurosMulta: number;              // brutoConciliated − totalInstallmentValue
  brutoConciliated: number;             // Σ item.total dos créditos

  // ── BLOCO 2b: Deduções do DRE ────────────────────────────────────────────
  // NOTA: item.tax = total − net_total = TOTAL das deduções do gateway.
  //       antecipation_gap NÃO entra (derivado algebrico, causaria dupla contagem).
  deducoes: {
    jurosParcelamento: number;          // Σ item.fees
    taxaTotal: number;                  // Σ item.tax  (total gateway)
    taxaAdministrativa: number;         // Σ item.administration_tax
    taxaCartao: number;                 // Σ item.transaction_tax
    outrasDeducoes: number;             // taxaTotal − juros − admin − cartao (residual: inclui taxa de venda 5%)
  };

  // ── BLOCO 3: Resultado e fluxo ────────────────────────────────────────────
  liquidoCreditEntries: number;         // Σ net_total dos créditos (puro, sem ajuste CB)
  liquidoTotal: number;                 // liquidoCreditEntries − netChargebackImpact
  chargebackDebitTotal: number;
  chargebackRefundTotal: number;
  netChargebackImpact: number;

  // ── BLOCO 4: Saques ───────────────────────────────────────────────────────
  saqueEntries: InvoiceFinanceItem[];
  totalSacado: number;
  totalTaxasTED: number;
  saldoAtual: number;

  // ── BLOCO 5: Chargebacks detalhados ──────────────────────────────────────
  chargebacks: InvoiceChargebackMatch[];
  totalCustoOperacaoChargebacks: number;
  totalPrejuizoChargebacks: number;

  // ── Créditos órfãos ───────────────────────────────────────────────────────
  creditosOrfaos: InvoiceFinanceItem[];
  totalCreditosOrfaos: number;

  // ── Inconsistências de saldo ──────────────────────────────────────────────
  saldoInconsistencies: Array<{
    item: InvoiceFinanceItem;
    prevSaldo: number;
    expectedSaldo: number;
    diff: number;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────

const PAID_METHODS_INVOICE = new Set([
  'pix', 'boleto', 'boleto_recurring', 'credit_card', 'credit_card_recurring',
]);

function normalizeMethodInvoice(method?: string | null): string {
  if (!method) return '';
  const m = method.toLowerCase().trim();
  if (m.includes('credit') || m.includes('crédit')) return 'credit_card';
  return m;
}

function addBusinessDays(date: Date, days: number): Date {
  const r = new Date(date);
  let added = 0;
  while (added < days) {
    r.setDate(r.getDate() + 1);
    const d = r.getDay();
    if (d !== 0 && d !== 6) added++;
  }
  return r;
}

function estimateAvailableDateInvoice(sale: InvoiceSaleItem): Date | null {
  const base = sale.approved_at ? new Date(sale.approved_at) : null;
  if (!base) return null;
  const m = normalizeMethodInvoice(sale.payment_method_id);
  if (m.includes('credit_card')) { const d = new Date(base); d.setDate(d.getDate() + 32); return d; }
  if (m === 'boleto' || m === 'boleto_recurring') return addBusinessDays(base, 3);
  if (m === 'pix') return addBusinessDays(base, 1);
  return null;
}

function n(v: number | null | undefined): number {
  return Number(v) || 0;
}

// ─────────────────────────────────────────────────────────────────────────────
//  buildReport
// ─────────────────────────────────────────────────────────────────────────────

export function buildReport(
  sales: InvoiceSaleItem[],
  finance: InvoiceFinanceItem[],
): InvoiceReport {
  const now = new Date();

  // ── Índices para lookup O(1) ──────────────────────────────────────────────
  // Em faturas a chave de conciliação primária é invoice_hash; sale_hash é fallback
  const financeByHash = new Map<string, InvoiceFinanceItem[]>();
  for (const f of finance) {
    if (!f.hash) continue;
    if (!financeByHash.has(f.hash)) financeByHash.set(f.hash, []);
    financeByHash.get(f.hash)!.push(f);
  }

  const saleByHash = new Map<string, InvoiceSaleItem>();
  for (const s of sales) {
    if (s.invoice_hash) saleByHash.set(s.invoice_hash, s);
    if (s.sale_hash)    saleByHash.set(s.sale_hash, s);
  }

  // ── BLOCO 1: Vendas pagas ─────────────────────────────────────────────────
  const paidSales = sales.filter(
    s => s.payment_status_id === 'paid' &&
         PAID_METHODS_INVOICE.has(normalizeMethodInvoice(s.payment_method_id)) &&
         n(s.installment_value) > 0
  );

  const salesNotConciliated: InvoiceNotConciliatedSale[] = [];
  for (const sale of paidSales) {
    const key1   = sale.invoice_hash ?? '';
    const key2   = sale.sale_hash    ?? '';
    const entries = [...(financeByHash.get(key1) ?? []), ...(financeByHash.get(key2) ?? [])];
    const creditEntry = entries.find(e => e.operation_type_raw === 'credit');
    if (!creditEntry) {
      const estimatedAvailableAt = estimateAvailableDateInvoice(sale);
      salesNotConciliated.push({
        sale,
        estimatedAvailableAt,
        isPastDeadline: estimatedAvailableAt ? estimatedAvailableAt <= now : false,
      });
    }
  }

  // Créditos do extrato — ajusta total se backend embutiu juros/multa
  const creditEntries = finance
    .filter(f => f.operation_type_raw === 'credit')
    .map(f => {
      const base = n(f.installment_value) + n(f.juros) + n(f.multa);
      return base > n(f.total) ? { ...f, total: base } : f;
    });

  const salesCancelledInFinance: InvoiceFinanceItem[] = [];
  for (const entry of creditEntries) {
    const sale = saleByHash.get(entry.hash);
    if (sale && sale.payment_status_id !== 'paid') salesCancelledInFinance.push(entry);
  }

  const creditosOrfaos      = creditEntries.filter(entry => !saleByHash.has(entry.hash));
  const totalCreditosOrfaos = creditosOrfaos.reduce((a, f) => a + n(f.total), 0);

  const totalSalesInstallmentValue = paidSales.reduce((a, s) => a + n(s.installment_value), 0);
  const totalNotConciliated        = salesNotConciliated.reduce((a, s) => a + n(s.sale.installment_value), 0);
  const totalCancelledInFinance    = salesCancelledInFinance.reduce((a, f) => a + n(f.total), 0);

  const totalConciliated = creditEntries
    .filter(f => { const s = saleByHash.get(f.hash); return !s || s.payment_status_id === 'paid'; })
    .reduce((a, f) => a + n(f.total), 0);

  // ── BLOCO 2: Composição do bruto ──────────────────────────────────────────
  const brutoConciliated      = creditEntries.reduce((a, f) => a + n(f.total), 0);
  const totalInstallmentValue = creditEntries.reduce((a, f) => a + n(f.installment_value), 0);
  const totalJurosMulta       = brutoConciliated - totalInstallmentValue;

  // ── BLOCO 2b: Deduções ────────────────────────────────────────────────────
  const taxaTotal          = creditEntries.reduce((a, f) => a + n(f.tax), 0);
  const jurosParcelamento  = creditEntries.reduce((a, f) => a + n(f.fees), 0);
  const taxaAdministrativa = creditEntries.reduce((a, f) => a + n(f.administration_tax), 0);
  const taxaCartao         = creditEntries.reduce((a, f) => a + n(f.transaction_tax), 0);
  const outrasDeducoes     = taxaTotal - jurosParcelamento - taxaAdministrativa - taxaCartao;

  // ── BLOCO 3: Chargebacks ─────────────────────────────────────────────────
  const chargebackDebits  = finance.filter(f => f.operation_type_raw === 'chargeback' || f.operation_type_raw === 'refund');
  const chargebackRefunds = finance.filter(f => f.operation_type_raw === 'chargeback_refund');

  const chargebackDebitTotal  = chargebackDebits.reduce((a, f) => a + Math.abs(n(f.net_total)), 0);
  const chargebackRefundTotal = chargebackRefunds.reduce((a, f) => a + n(f.net_total), 0);
  const netChargebackImpact   = chargebackDebitTotal - chargebackRefundTotal;

  const chargebacks: InvoiceChargebackMatch[] = chargebackDebits.map(cbEntry => {
    const entries        = financeByHash.get(cbEntry.hash) ?? [];
    const originalCredit = entries.find(e => e.operation_type_raw === 'credit') ?? null;
    const refundEntry    = entries.find(e => e.operation_type_raw === 'chargeback_refund') ?? null;
    const sale           = saleByHash.get(cbEntry.hash) ?? null;

    const saleBruto          = n(sale?.installment_value ?? sale?.total);
    const creditoLiquido     = n(originalCredit?.net_total);
    const debitadoChargeback = Math.abs(n(cbEntry.net_total));
    const taxasDevolvidas    = n(refundEntry?.net_total);
    const custoOperacao      = debitadoChargeback - taxasDevolvidas - creditoLiquido;
    const prejuizoTotal      = saleBruto + Math.max(0, custoOperacao);

    return { sale, originalCredit, chargebackEntry: cbEntry, refundEntry, saleBruto, creditoLiquido, debitadoChargeback, taxasDevolvidas, custoOperacao, prejuizoTotal };
  });

  const totalCustoOperacaoChargebacks = chargebacks.reduce((a, c) => a + Math.max(0, c.custoOperacao), 0);
  const totalPrejuizoChargebacks      = chargebacks.reduce((a, c) => a + c.prejuizoTotal, 0);

  const liquidoCreditEntries = creditEntries.reduce((a, f) => a + n(f.net_total), 0);
  const liquidoTotal         = liquidoCreditEntries - netChargebackImpact;

  // ── BLOCO 4: Saques ───────────────────────────────────────────────────────
  const saqueEntries  = finance.filter(f => f.operation_type_raw === 'ted' || (f as any).type === 'ted');
  const totalTaxasTED = saqueEntries.reduce((a, f) => a + n(f.operation_fee), 0);
  const totalSacado   = saqueEntries.reduce((a, f) => a + Math.abs(n(f.net_total)), 0) + totalTaxasTED;
  const saldoAtual    = finance.length > 0 ? finance[finance.length - 1].saldo : 0;

  // ── Inconsistências de saldo ──────────────────────────────────────────────
  const SALDO_TOLERANCIA = 1;
  const saldoInconsistencies: InvoiceReport['saldoInconsistencies'] = [];
  const financeOrdenado = [...finance].sort(
    (a, b) => new Date(a.dateRef ?? a.date).getTime() - new Date(b.dateRef ?? b.date).getTime()
  );
  let prevSaldo = 0;
  for (const item of financeOrdenado) {
    if (item.operation_type_raw == null) { prevSaldo = item.saldo ?? prevSaldo; continue; }
    const isTed      = item.operation_type_raw === 'ted' || (item as any).type === 'ted';
    const netEfetivo = isTed ? n(item.net_total) - n(item.operation_fee) : n(item.net_total);
    const expectedSaldo = prevSaldo + netEfetivo;
    const realSaldo     = item.saldo ?? 0;
    const diff          = realSaldo - expectedSaldo;
    if (Math.abs(diff) > SALDO_TOLERANCIA) {
      saldoInconsistencies.push({ item, prevSaldo, expectedSaldo, diff });
    }
    prevSaldo = realSaldo;
  }

  return {
    totalSalesInstallmentValue,
    totalConciliated,
    totalNotConciliated,
    totalCancelledInFinance,
    salesNotConciliated,
    salesCancelledInFinance,

    totalInstallmentValue,
    totalJurosMulta,
    brutoConciliated,

    deducoes: { jurosParcelamento, taxaTotal, taxaAdministrativa, taxaCartao, outrasDeducoes },

    liquidoCreditEntries,
    liquidoTotal,
    chargebackDebitTotal,
    chargebackRefundTotal,
    netChargebackImpact,

    saqueEntries,
    totalSacado,
    totalTaxasTED,
    saldoAtual,

    chargebacks,
    totalCustoOperacaoChargebacks,
    totalPrejuizoChargebacks,

    creditosOrfaos,
    totalCreditosOrfaos,

    saldoInconsistencies,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
//  Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useFinanceCheckFaturas() {
  const [salesData, setSalesData]     = useState<InvoiceSaleItem[]>([]);
  const [financeData, setFinanceData] = useState<InvoiceFinanceItem[]>([]);
  const [report, setReport]           = useState<InvoiceReport | null>(null);
  const [loading, setLoading]         = useState(false);

  const loadGlobal = async (project_id: string) => {
    setLoading(true);
    try {
      const [checkRes, salesRes] = await Promise.all([
        api.get(`/check-saldo-static/${project_id}/faturas/SaleInvoice/json`),
        api.get(`/sales-invoice`, { params: { limitItems: 100000, where: { project_id } } }),
      ]);

      const finance: InvoiceFinanceItem[] = checkRes?.data?.rows ?? [];
      const sales:   InvoiceSaleItem[]    = salesRes?.data?.rows ?? [];

      // Enriquecer entradas do extrato com client_id da venda correspondente
      for (let i = 0; i < finance.length; i++) {
        const item = finance[i];
        const sale = sales.find(
          s => (s.invoice_hash && s.invoice_hash === item.hash) ||
               (s.sale_hash    && s.sale_hash    === item.hash)
        );
        if (sale) {
          const cid = (sale as any).client_id ?? (sale as any).payer_id;
          if (cid) (finance[i] as any).client_id = cid;
        }
      }

      setSalesData(sales);
      setFinanceData(finance);
      setReport(buildReport(sales, finance));
    } finally {
      setLoading(false);
    }
  };

  return { loadGlobal, salesData, financeData, report, loading };
}
